import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import dayjs from 'dayjs';
import { Flex, Typography } from 'antd';

import themeToken from '@lib/theme/tokens/index';
import { StyledDrawer } from '@/features/captable/components/Shared/StyledDrawer';
import CloseIcon from '@/components/Icons/CloseIcon';
import { usePermissionsStore } from '@/stores/Permissions';
import { useSelectedCompanyStore } from '@/stores/selectedCompany';

import { useGenerateFileUrl } from '../api/generateFileUrl';
import FileShare from './FileShare';
import FileDownload from './DownloadFile';
import FileDelete from './FileDelete';
import FilesIcons from './FilesIcons';
import { createAnalyticsKeepalive, useCreateAnalytics } from '../api/createAnalytics';
import EditFile from './EditFile';
import PdfContainer, { PdfContainerHandle, PdfEngagementSnapshot } from './PdfContainer';
import type { CreateAnalyticsPayload, FolderData } from '../types';
import { isOfficeViewerSupportedByMimeOrExtension } from '../utils/IsOfficeViewerSupported';
import { useUserDataStore } from '@/stores/UserData';
import { queryClient } from '@/lib/react-query';

const { Text } = Typography;

const BodyWrapper = styled('div')`
  background: ${themeToken['branding-natural-5']};
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

type Props = {
  open: boolean;
  isFileDownloading: boolean;
  setOpen: (open: boolean) => void;
  file: FolderData;
  parentFolder: FolderData;
  userEmail?: string;
  uuid?: string;
  handleDownloadStart: () => void;
  handleDownloadEnd: () => void;
  setIsShareFileOpen: () => void;
  setIsDeleteModalOpen: () => void;
  setIsEditModalOpen: () => void;
  setPreviewOpenDate: Dispatch<SetStateAction<Date | undefined>>;
};

const FilePreview = ({
  open = false,
  setOpen,
  file,
  uuid,
  isFileDownloading,
  parentFolder,
  userEmail,
  handleDownloadStart,
  handleDownloadEnd,
  setIsShareFileOpen,
  setIsDeleteModalOpen,
  setIsEditModalOpen,
  setPreviewOpenDate,
}: Props) => {
  const { t, i18n } = useTranslation('dataroom');
  dayjs.locale(i18n.language);
  const { permissions } = usePermissionsStore((state) => state);
  const { selectedCompany } = useSelectedCompanyStore((state) => state);
  const { userData } = useUserDataStore((state) => state);
  const [fileExtension, setFileExtension] = useState('');
  const pdfContainerRef = useRef<PdfContainerHandle>(null);
  const engagementSessionRef = useRef('');
  const previewOpenedAtRef = useRef<Date>();
  const latestSnapshotRef = useRef<PdfEngagementSnapshot>({ totalPages: 0, pages: [] });
  const hasSubmittedAnalyticsRef = useRef(false);
  const wasOpenRef = useRef(open);
  const submitAnalyticsRef = useRef<() => void>(() => undefined);
  // get current window url
  const { pathname } = window.location;
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  const { data } = useGenerateFileUrl({
    company_id: selectedCompany?.cid as number,
    file_id: file.id,
    uuid,
  });

  const { mutate: createAnalyticsMutation } = useCreateAnalytics();
  const isPdf = fileExtension.toLowerCase() === 'pdf';

  const submitAnalytics = useCallback(
    (snapshot?: PdfEngagementSnapshot, reliable = false, final = false) => {
      const previewOpenedAt = previewOpenedAtRef.current;
      if (!previewOpenedAt || (final && hasSubmittedAnalyticsRef.current)) return;
      if (final) hasSubmittedAnalyticsRef.current = true;

      const engagement = snapshot ?? latestSnapshotRef.current;
      const payload: CreateAnalyticsPayload = {
        ...(uuid ? { hash: uuid } : {}),
        file_id: file.id,
        duration: (Date.now() - previewOpenedAt.getTime()) / 1000,
        downloaded: 0,
        viewed: 1,
        ...(isPdf && engagement.totalPages > 0
          ? {
              session_id: engagementSessionRef.current,
              tracking_type: 'pdf_scroll' as const,
              total_pages: engagement.totalPages,
              pages: engagement.pages,
            }
          : {}),
      };
      const params = { company_id: selectedCompany?.cid as number, payload };

      if (reliable) {
        void createAnalyticsKeepalive(params)
          .then(() =>
            Promise.all([
              queryClient.invalidateQueries({ queryKey: ['analytics'] }),
              queryClient.invalidateQueries({ queryKey: ['analytics-heatmap'] }),
            ]),
          )
          .catch(() => undefined);
      } else createAnalyticsMutation(params);
    },
    [createAnalyticsMutation, file.id, isPdf, selectedCompany?.cid, uuid],
  );

  const handleEngagementChange = useCallback(
    (snapshot: PdfEngagementSnapshot, reliable = false) => {
      latestSnapshotRef.current = snapshot;
      submitAnalytics(snapshot, reliable);
    },
    [submitAnalytics],
  );

  submitAnalyticsRef.current = () => {
    const snapshot = pdfContainerRef.current?.getEngagementSnapshot() ?? latestSnapshotRef.current;
    latestSnapshotRef.current = snapshot;
    submitAnalytics(snapshot, true, true);
  };

  const handleClose = () => {
    submitAnalyticsRef.current();
    setOpen(false);
  };

  useEffect(() => {
    if (data) {
      setFileExtension(data?.data.file_extension);
    }
  }, [data]);

  useEffect(() => {
    if (setPreviewOpenDate && open) {
      const openedAt = new Date();
      engagementSessionRef.current = crypto.randomUUID();
      previewOpenedAtRef.current = openedAt;
      latestSnapshotRef.current = { totalPages: 0, pages: [] };
      hasSubmittedAnalyticsRef.current = false;
      setPreviewOpenDate(openedAt);
    }
  }, [setPreviewOpenDate, open]);

  useEffect(() => {
    if (wasOpenRef.current && !open) submitAnalyticsRef.current();
    wasOpenRef.current = open;
  }, [open]);

  useEffect(() => () => submitAnalyticsRef.current(), []);

  useEffect(() => {
    // Handler to call on window resize
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Call handler once to set the initial size
    handleResize();

    // Remove event listener on cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const drawWatermarkInExcel = () => {
    if (parentFolder && !parentFolder.watermarked) {
      return null;
    }
    const rows = 4;
    const cols = 4;
    const watermarks = [];

    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        watermarks.push(
          <div
            key={`watermark-${row}-${col}`}
            style={{
              position: 'absolute',
              top: `${10 + row * 20}%`,
              left: `${15 + col * 25}%`,
              transform: 'translate(-50%, -50%)',
              fontSize: '24px',
              color: themeToken['branding-primary-6'],
              fontWeight: 600,
              textAlign: 'center',
              pointerEvents: 'none',
              userSelect: 'none',
              opacity: 0.2,
              whiteSpace: 'nowrap',
            }}
          >
            {pathname.includes('shared') ? userEmail : userData?.userInfo.mail}
          </div>,
        );
      }
    }

    return watermarks;
  };

  const viewFileUrl = () => {
    if (data) {
      if (fileExtension === 'pdf') {
        return (
          <PdfContainer
            ref={pdfContainerRef}
            fileUrl={data?.data.url}
            windowWidth={windowSize.width}
            onEngagementChange={handleEngagementChange}
          />
        );
      } else if (isOfficeViewerSupportedByMimeOrExtension(data?.data.file_extension)) {
        return (
          <div style={{ position: 'relative', width: '100%', height: '100%' }}>
            <iframe
              title="file preview"
              src={`https://view.officeapps.live.com/op/embed.aspx?src=${data?.data.url}`}
              width="100%"
              height="100%"
              style={{
                border: 0,
              }}
              allowFullScreen={true}
            >
              {!fileExtension?.includes('pdf') && (
                <>
                  This is an embedded
                  <a target="_blank" rel="noreferrer" href="http://office.com">
                    Microsoft Office
                  </a>
                  document, powered by
                  <a target="_blank" rel="noreferrer" href="http://office.com/webapps">
                    Office Online
                  </a>
                </>
              )}
            </iframe>
            {drawWatermarkInExcel()}
          </div>
        );
      } else {
        return (
          <div style={{ position: 'relative', width: '100%', height: '100%' }}>
            <iframe
              title="file preview"
              allowFullScreen={true}
              sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
              src={data?.data.url}
              width="100%"
              height="100%"
              style={{ border: 0 }}
            >
              {t('unsupportedFileType')}
            </iframe>
          </div>
        );
      }
    }
  };

  return (
    <StyledDrawer
      closable={false}
      className="drawer"
      width={windowSize.width > 768 ? '90%' : '100%'}
      placement="right"
      size="large"
      onClose={handleClose}
      open={open}
    >
      <Flex
        className="drawer-header"
        justify="space-between"
        align="center"
        onContextMenu={(ev) => ev.preventDefault()}
      >
        <Flex align="center" gap={14}>
          <CloseIcon onClick={handleClose} />
          <FilesIcons mimeType={file?.file_mimetype.toLocaleLowerCase()} />
          <Flex vertical>
            <Text
              style={{
                fontSize: themeToken.fontSizeLG,
                color: themeToken['branding-primary-6'],
                fontWeight: 600,
                marginBottom: themeToken.marginXXS,
              }}
            >
              {file?.name}
            </Text>
            <Text
              style={{
                color: themeToken['branding-natural-6'],
                fontSize: themeToken.fontSize,
              }}
            >
              {`${t('fileName')}, ${dayjs(file?.created).format('MMMM D, YYYY, hh:mm A')}`}
            </Text>
          </Flex>
        </Flex>
        <Flex gap={8}>
          {!uuid && (
            <FileShare
              hasLabel={false}
              disabled={!permissions.includes('company.dataroom.management')}
              setIsShareFileOpen={setIsShareFileOpen}
            />
          )}

          <FileDownload
            hasLabel={false}
            fileId={file.id}
            file={file}
            entityType="file"
            uuid={uuid}
            disabled={
              (uuid ? !file.downloadable : !permissions.includes('company.dataroom.download')) || isFileDownloading
            }
            handleDownloadStart={handleDownloadStart}
            handleDownloadEnd={handleDownloadEnd}
          />

          {!uuid && (
            <FileDelete
              hasLabel={false}
              setIsDeleteModalOpen={setIsDeleteModalOpen}
              disabled={!permissions.includes('company.dataroom.management')}
            />
          )}

          {!uuid && (
            <EditFile
              hasLabel={false}
              disabled={!permissions.includes('company.dataroom.management')}
              setIsEditModalOpen={setIsEditModalOpen}
            />
          )}
        </Flex>
      </Flex>

      <Flex vertical className="drawer-body" onContextMenu={(ev) => ev.preventDefault()}>
        <BodyWrapper
          style={{
            overflowY: 'scroll',
            position: 'relative',
            width: '100%',
          }}
          onContextMenu={(ev) => ev.preventDefault()}
        >
          {viewFileUrl()}
        </BodyWrapper>
      </Flex>
    </StyledDrawer>
  );
};

export default FilePreview;
